import { useMemo, useState } from 'react';
import { LayoutGrid } from 'lucide-react';
import { Button } from '../ui/button';
import { ConfirmDeleteDialog } from '../ui/confirm-delete-dialog';
import { useNotifications } from '../../store/useStore';
import { conteggiPerSessione, proponiAssegnazione } from '@eventi/assegna.ts';
import { REGOLE_PREDEFINITE, type Percorso, type ProgrammaEvento } from '@eventi/tipi.ts';
import { eventiService } from '../../lib/eventi/eventiService';
import { messaggioErrore } from '../../lib/eventi/errori';
import {
  bozzeDaSalvare,
  confermabili,
  prontiDaConfermare,
  righeSenzaPosto,
  senzaPostoDopoModifica,
  type RigaSenzaPosto,
} from '../../lib/eventi/assegnazioni';
import { nomeAula, programmaPronto } from '../../lib/eventi/programma';
import type { Evento, PartecipanteEvento } from '../../lib/eventi/tipi';
import { TabelloneAule } from './TabelloneAule';
import { ElencoAssegnazioni } from './ElencoAssegnazioni';
import { DaSistemare } from './DaSistemare';
import { ModificaPercorsoDialog } from './ModificaPercorsoDialog';
import { ConfermaInvioDialog } from './ConfermaInvioDialog';
import { PassiAssegnazione } from './PassiAssegnazione';
import type { SchedaEvento } from './schede';

type Props = {
  evento: Evento;
  programma: ProgrammaEvento;
  partecipanti: PartecipanteEvento[];
  onPartecipantiCambiati: () => Promise<void>;
  onApri: (scheda: SchedaEvento) => void;
};

/**
 * La scheda Assegnazioni: il tabellone delle aule, la proposta automatica,
 * le modifiche a mano e la conferma. Solo la conferma comunica qualcosa ai
 * partecipanti: proposta e modifiche restano bozze.
 */
export function SchedaAssegnazioni({ evento, programma, partecipanti, onPartecipantiCambiati, onApri }: Props) {
  const { addNotification } = useNotifications();
  const [sessione, setSessione] = useState<string | null>(null);
  const [selezionati, setSelezionati] = useState<Set<string>>(new Set());
  const [inModifica, setInModifica] = useState<PartecipanteEvento | null>(null);
  const [daConfermare, setDaConfermare] = useState<PartecipanteEvento[]>([]);
  const [confermando, setConfermando] = useState(false);
  const [chiediProposta, setChiediProposta] = useState(false);
  const [proponendo, setProponendo] = useState(false);
  const [senzaPosto, setSenzaPosto] = useState<RigaSenzaPosto[] | null>(null);

  const regole = evento.regole ?? REGOLE_PREDEFINITE;
  const conteggi = useMemo(() => conteggiPerSessione(programma, partecipanti), [programma, partecipanti]);
  const pronti = useMemo(() => prontiDaConfermare(programma, partecipanti), [programma, partecipanti]);
  const bozze = useMemo(() => partecipanti.filter((p) => p.assegnazione_stato === 'bozza').length, [partecipanti]);
  // Dopo una rilettura restano spuntate solo le bozze che si possono ancora confermare.
  const scelti = useMemo(() => confermabili(partecipanti).filter((p) => selezionati.has(p.id)), [partecipanti, selezionati]);
  const sessioneFiltro = useMemo(() => {
    const s = programma.sessioni.find((x) => x.codice === sessione);
    if (!s) return null;
    const giorno = programma.giorni.find((g) => g.codice === s.giorno)?.nome ?? s.giorno;
    return { codice: s.codice, nome: `${nomeAula(programma, s.aula)} · ${giorno}` };
  }, [programma, sessione]);

  const ricarica = async () => {
    try {
      await onPartecipantiCambiati();
    } catch (e) {
      addNotification({ type: 'error', title: 'Non riesco a rileggere i partecipanti', message: messaggioErrore(e) });
    }
  };

  const proponi = async () => {
    setProponendo(true);
    try {
      // Si parte dai dati di adesso, non da quelli sullo schermo: un collega potrebbe aver confermato qualcuno.
      const freschi = await eventiService.partecipanti(evento.id);
      const proposta = proponiAssegnazione(programma, freschi);
      const righe = bozzeDaSalvare(proposta.bozze, freschi);
      await eventiService.salvaBozze(evento.id, righe);
      setSenzaPosto(righeSenzaPosto(programma, proposta.senzaPosto, freschi));
      setChiediProposta(false);
      addNotification({
        type: 'success',
        title: 'Proposta pronta',
        message: `${proposta.bozze.size} persone in bozza (${righe.length} cambiate). Nessuna email e' partita.`,
      });
    } catch (e) {
      addNotification({ type: 'error', title: 'Proposta non riuscita', message: messaggioErrore(e) });
    } finally {
      setProponendo(false);
      await ricarica();
    }
  };

  const salvaModifica = async (percorso: Percorso) => {
    if (!inModifica) return;
    try {
      await eventiService.salvaAssegnazione(evento.id, inModifica.id, percorso);
      setSenzaPosto((prima) => (prima ? senzaPostoDopoModifica(prima, inModifica.id, percorso) : prima));
      addNotification({ type: 'success', title: 'Assegnazione salvata in bozza', message: inModifica.nome });
      setInModifica(null);
      await ricarica();
    } catch (e) {
      addNotification({ type: 'error', title: 'Non riesco a salvare', message: messaggioErrore(e) });
    }
  };

  const conferma = async () => {
    setConfermando(true);
    try {
      const n = await eventiService.conferma(evento.id, daConfermare.map((p) => p.id));
      addNotification({
        type: 'success',
        title: `${n} ${n === 1 ? 'persona confermata' : 'persone confermate'}`,
        message: evento.etichetta_assegnata_id
          ? "L'email con il percorso parte da sola (lista «percorso assegnato»)."
          : "Nessuna email: nelle Impostazioni manca l'etichetta «percorso assegnato».",
      });
      setDaConfermare([]);
      setSelezionati(new Set());
    } catch (e) {
      addNotification({ type: 'error', title: 'Conferma non riuscita', message: messaggioErrore(e) });
    } finally {
      setConfermando(false);
      await ricarica();
    }
  };

  if (!programmaPronto(programma)) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-white py-14 text-center">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-neutral-400">
          <LayoutGrid className="h-6 w-6" />
        </span>
        <p className="mt-3 text-sm font-medium text-neutral-700">Manca il programma</p>
        <p className="mx-auto mt-1 max-w-md text-xs text-neutral-500">Importalo nella scheda Programma: poi qui compare il tabellone delle aule.</p>
        <Button variant="outline" className="mt-4" onClick={() => onApri('programma')}>
          Vai al programma
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-5">
      <PassiAssegnazione
        bozze={bozze}
        pronti={pronti.length}
        scelti={scelti.length}
        onProponi={() => setChiediProposta(true)}
        onConfermaPronti={() => setDaConfermare(pronti)}
        onConfermaScelti={() => setDaConfermare(scelti)}
      />

      {senzaPosto && (
        <DaSistemare
          righe={senzaPosto}
          onNascondi={() => setSenzaPosto(null)}
          onSistema={(id) => setInModifica(partecipanti.find((p) => p.id === id) ?? null)}
        />
      )}

      <TabelloneAule programma={programma} conteggi={conteggi} sessioneScelta={sessione} onScegliSessione={setSessione} />

      <ElencoAssegnazioni
        programma={programma}
        partecipanti={partecipanti}
        selezionati={selezionati}
        onSelezione={setSelezionati}
        onModifica={setInModifica}
        sessione={sessioneFiltro}
        onTogliSessione={() => setSessione(null)}
      />

      {inModifica && (
        <ModificaPercorsoDialog
          key={inModifica.id}
          partecipante={inModifica}
          programma={programma}
          regole={regole}
          conteggi={conteggi}
          onChiudi={() => setInModifica(null)}
          onSalva={salvaModifica}
        />
      )}

      <ConfermaInvioDialog
        persone={daConfermare}
        programma={programma}
        conEmail={Boolean(evento.etichetta_assegnata_id)}
        inCorso={confermando}
        onConferma={conferma}
        onChiudi={() => setDaConfermare([])}
      />

      <ConfirmDeleteDialog
        open={chiediProposta}
        onOpenChange={(v) => !v && !proponendo && setChiediProposta(false)}
        onConfirm={proponi}
        isDeleting={proponendo}
        title="Proporre l'assegnazione?"
        description="Assegna a chi non e' confermato quello che ha chiesto, in ordine di arrivo, finche' ci sono posti. Le bozze attuali, anche quelle ritoccate a mano, vengono sostituite; i confermati non si toccano. Nessuna email parte adesso."
        confirmLabel="Proponi"
        busyLabel="Preparo la proposta…"
        confirmClassName="bg-primary-600 hover:bg-primary-700 text-white"
      />
    </div>
  );
}
